import { createSelector } from '@reduxjs/toolkit';

import { getGlobalDepartments } from 'utils/personnelHelper';
import { transformOperational } from 'utils/DataTransform/CleanerTask/CleanerTaskList';
import {
  getGlobalDepartmentRecords,
  getGlobalSiteList,
  getGlobalUserRecords,
} from 'selectors/globalSelectors';
import { getTimezone } from 'reducers/utils';
import dayjs from 'dayjs';
import {
  getGlobalBusinessClientRecord,
  getGlobalSiteRecord,
  getGlobalUsersRecord,
} from 'reducers/globalList';
import { getAreaOfOperation } from 'reducers/securityCompany';
import { getUserLocation } from 'reducers/users';
import { getRegionCentreOfGravity } from 'reducers/region';
import {
  transformDetails,
  transformRawDetails,
} from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';
import { selectors as dataGridTableSelectors } from '../Modules/dataGridTable';
import { selectors as filtersSelectors } from '../Modules/filters';
import { transform as siteListTransform } from 'utils/DataTransform/Sites/SitesList';
import { PAGE_URLS } from 'utils/constants/index.js';
import isEmpty from 'lodash/isEmpty';

export const getGridRowDetailsTransformed = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  dataGridTableSelectors.getDummyRowDetails,
  getTimezone,
  getGlobalUsersRecord,
  getGlobalSiteRecord,
  getGlobalBusinessClientRecord,
  getGlobalSiteList,
  getAreaOfOperation,
  getUserLocation,
  getRegionCentreOfGravity,
  getGlobalDepartmentRecords,
  (
    CleaningDetails,
    dummyTaskDetails,
    timeZone,
    users,
    sites,
    businessClients,
    siteList,
    areaOfOperation,
    userLocation,
    regionCenterOfGravity,
    globalDepartmentRecords,
  ) => {
    if (!CleaningDetails || Object.keys(CleaningDetails).length === 0) {
      return {};
    }
    const cog = {
      lat: areaOfOperation.centreOfGravity.latitude,
      lng: areaOfOperation.centreOfGravity.longitude,
    };
    const centerOfGravity = userLocation || cog || regionCenterOfGravity;
    const globalDepartmentList = getGlobalDepartments(globalDepartmentRecords);
    // return CleaningObject.details;
    const taskList = transformDetails({
      id: CleaningDetails.rootTaskId,
      tasks: CleaningDetails.tasks,
      joins: CleaningDetails.joins,
      surfaces: CleaningDetails.surfaces,
      users,
      dummyTaskData: dummyTaskDetails,
      sites,
      businessClients,
      siteList: siteListTransform(siteList, globalDepartmentList),
      centerOfGravity,
      globalDepartmentList,
      pageName: PAGE_URLS.CLEANING_TASKS.link,
    });
    return taskList;
  },
);

export const getCompletedEvidences = createSelector(
  getGridRowDetailsTransformed,
  rowDetails => {
    if (isEmpty(rowDetails)) {
      return [];
    }
    return rowDetails.others.evidenceCollection.value;
  },
);

export const getDetailsTransformedRaw = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  dataGridTableSelectors.getDummyRowDetails,
  getGlobalDepartmentRecords,
  getGlobalSiteList,
  getGlobalUsersRecord,

  (
    CleaningDetails,
    dummyTaskDetails,
    globalDepartmentRecords,
    siteList,
    users,
  ) => {
    if (!CleaningDetails || Object.keys(CleaningDetails).length === 0) {
      return {};
    }
    const { tasks } = CleaningDetails;
    const { joins } = CleaningDetails;
    const { surfaces } = CleaningDetails;

    const globalDepartmentList = getGlobalDepartments(globalDepartmentRecords);

    return transformRawDetails({
      tasks: { tasks, joins, surfaces },
      dummyTaskData: dummyTaskDetails,
      globalDepartmentList,
      siteList,
      users,
    });
  },
);

export const getGridListTransformed = createSelector(
  dataGridTableSelectors.getGridTableList,
  getGlobalDepartmentRecords,
  getTimezone,
  getGlobalUserRecords,
  (CleaningList, globalDepartmentRecords, timezone, users) => {
    const globalDepartmentList = getGlobalDepartments(globalDepartmentRecords);

    const taskList = transformOperational(
      CleaningList,
      dayjs(),
      users,
      globalDepartmentList,
      PAGE_URLS.CLEANING_TASKS.link,
    );
    return taskList;
  },
);

export const getTotalRowsCount = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => rowsCount,
);
export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);

export const getRowDetailsUnTransformed = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  rowDetails => rowDetails,
);

export const getCleaningFilters = createSelector(
  filtersSelectors.getFilterValues,
  filterValues => filterValues,
);

export const getCleaningFilterKey = createSelector(
  filtersSelectors.getFilterKey,
  filterKey => filterKey,
);
export const getCleaningTaskList = createSelector(
  dataGridTableSelectors.getGridTableList,
  cleaningList => cleaningList,
);
export const getSelectedRowID = createSelector(
  dataGridTableSelectors.getSelectedRowID,
  selectedRowID => selectedRowID,
);
